import { parseDateSafe } from 'utils/timeHelper';
import {
  startOfDay,
  differenceInCalendarDays,
  intervalToDuration,
} from 'date-fns';
import common from 'constants/common';
import {
  isRpmModule,
  parseDateStringWithoutTimeZoneAdopt,
} from 'infrastructure/functions';
import { isUndefined } from 'lodash';
import { generateFullName } from 'utils/badgeHelpers';
import { useGetConditions } from 'infrastructure/hooks/ccm';
import { ClinicStatus, LoginStatus, ModuleType } from 'infrastructure/enums';
import { useTranslation } from 'react-i18next';

import { useBannerInfo } from './hooks/useBannerInfo';
import CombineMobileBanner from './components/combine-mobile-banner';
import CombinePatientBanner from './components/combine-banner';
import PatientMiniTopBanner from '../patientMiniTopBanner';
import s from './styles.module.scss';

import type { RpmStatuses } from 'infrastructure/enums';
import type { IRpmPatientAgent } from 'infrastructure/interfaces';
import type { FC } from 'react';

interface CcmRpmPatientBannerProps {
  patientGuid: string;
  careAgents?: IRpmPatientAgent[];
  patientDetailsLoading?: boolean;
}

const CcmRpmPatientBanner: FC<CcmRpmPatientBannerProps> = (props) => {
  const { patientGuid, careAgents, patientDetailsLoading } = props;

  const module = ModuleType.RPM; // for status line

  const {
    birthDate,
    fullName,
    ccmStatus,
    rpmStatus,
    riskScore,
    serviceDeliveryModel,
    providerServiceType,
    isLoading,
    isShowEmailTag,
    sla,
    clinicName,
    physician,
    referralTypes,
    holdingStatus,
    pcmStatus,
    isShowMiniBanner,
    isShowDoNotCallTag,
    showSalesForceIntegration,
    disabledCreateInternalAdmissionForms,
    ccmHoldingStatus,
    consumablesArrivingDate,
    isMobileDevice,
    clinicStatus,
    physicianStatus,
    patientTZ,
    isPatient,
    pmpmStatus,
  } = useBannerInfo({
    patientGuid,
    module: ModuleType.CCM, // CCM module for loading questionnaire
  });

  const { goals: conditions, isLoading: conditionLoading } = useGetConditions({
    patientGuid,
    meta: { items: 1000, page: 1 },
  });

  const { t } = useTranslation();

  const today = startOfDay(new Date());
  const endDate = parseDateStringWithoutTimeZoneAdopt(holdingStatus?.endTime);

  const remainingDays = endDate && differenceInCalendarDays(endDate, today);

  const consumablesEndDate = parseDateSafe(consumablesArrivingDate);
  const consumablesRemainingDays =
    consumablesEndDate &&
    intervalToDuration({ start: today, end: consumablesEndDate })?.days;

  const ccmStatusObj = !isUndefined(ccmStatus)
    ? common.ccmStatus[ccmStatus]
    : undefined;
  const rpmStatusObj = !isUndefined(rpmStatus)
    ? common.statusPatients[
        rpmStatus as Exclude<RpmStatuses, RpmStatuses.PatientRefused>
      ]
    : undefined;
  const showHoldingStatusBadge =
    holdingStatus?.status !== 'active' &&
    !isUndefined(holdingStatus?.endTime) &&
    !isUndefined(remainingDays);

  const showTemporaryHoldBadge = ccmHoldingStatus;
  const statusColor = isRpmModule(module)
    ? rpmStatusObj?.color
    : ccmStatusObj?.color;

  const showConsumablesBadge =
    !isUndefined(consumablesArrivingDate) &&
    !isUndefined(consumablesRemainingDays);

  const careAgent = careAgents?.find(({ type }) => type === 'ccmManager');
  const careAgentName = careAgent
    ? generateFullName(
        careAgent.careAgentTitle,
        careAgent.careAgentFirstName,
        '',
        careAgent.careAgentLastName,
        careAgent.careAgentProfession,
      )
    : undefined;

  const clinicNameElement = (
    <>
      {clinicName}
      {(clinicStatus === ClinicStatus.Delinquent ||
        clinicStatus === ClinicStatus.Inactive) && (
        <span className={s.status}>
          ({t(`clinicStatuses.${clinicStatus}`)})
        </span>
      )}
    </>
  );

  const providerNameElement = (
    <>
      {physician}
      {(physicianStatus === LoginStatus.Inactive ||
        physicianStatus === LoginStatus.Blocked) && (
        <span className={s.status}>
          ({t(`loginStatuses.${physicianStatus}`)})
        </span>
      )}
    </>
  );

  return (
    <>
      {!isMobileDevice && (
        <CombinePatientBanner
          patientGuid={patientGuid}
          showConsumablesBadge={showConsumablesBadge}
          showHoldingStatusBadge={showHoldingStatusBadge}
          showTemporaryHoldBadge={showTemporaryHoldBadge}
          isLoading={isLoading}
          birthDate={birthDate}
          statusColor={statusColor}
          holdingStatus={holdingStatus}
          remainingDays={remainingDays ?? 0}
          isShowDoNotCallTag={isShowDoNotCallTag}
          showSalesForceIntegration={showSalesForceIntegration}
          isShowEmailTag={isShowEmailTag}
          serviceDeliveryModel={serviceDeliveryModel}
          providerServiceType={providerServiceType}
          pcmStatus={pcmStatus}
          pmpmStatus={pmpmStatus}
          sla={sla}
          fullName={fullName}
          clinicName={clinicNameElement}
          physician={providerNameElement}
          referralTypes={referralTypes}
          rpmStatusObj={rpmStatusObj}
          ccmStatusObj={ccmStatusObj}
          riskScore={riskScore}
          disabledCreateInternalAdmissionForms={
            disabledCreateInternalAdmissionForms
          }
          conditions={conditions?.items}
          careAgentName={careAgentName}
          conditionLoading={conditionLoading}
          patientDetailsLoading={patientDetailsLoading}
          consumablesRemainingDays={consumablesRemainingDays}
          patientTZ={patientTZ ?? ''}
        />
      )}
      {isShowMiniBanner && (
        <PatientMiniTopBanner
          fullName={fullName}
          riskScore={riskScore}
          isPatient={isPatient}
          clinicName={clinicNameElement}
          doNotCall={isShowDoNotCallTag}
          showEmailTag={isShowEmailTag}
          physician={providerNameElement}
          sla={sla}
          pcmStatus={pcmStatus}
          showSalesForceIntegration={showSalesForceIntegration}
          statusColor={statusColor}
          module={ModuleType.RPMCCM}
        />
      )}
      {isMobileDevice && (
        <CombineMobileBanner
          patientGuid={patientGuid}
          showHoldingStatusBadge={showHoldingStatusBadge}
          showTemporaryHoldBadge={showTemporaryHoldBadge}
          isLoading={isLoading}
          birthDate={birthDate}
          statusColor={statusColor}
          holdingStatus={holdingStatus}
          remainingDays={remainingDays ?? 0}
          isShowDoNotCallTag={isShowDoNotCallTag}
          isShowEmailTag={isShowEmailTag}
          serviceDeliveryModel={serviceDeliveryModel}
          providerServiceType={providerServiceType}
          pcmStatus={pcmStatus}
          showSalesForceIntegration={showSalesForceIntegration}
          pmpmStatus={pmpmStatus}
          sla={sla}
          fullName={fullName}
          clinicName={clinicNameElement}
          physician={providerNameElement}
          referralTypes={referralTypes}
          rpmStatusObj={rpmStatusObj}
          ccmStatusObj={ccmStatusObj}
          riskScore={riskScore}
          disabledCreateInternalAdmissionForms={
            disabledCreateInternalAdmissionForms
          }
          conditions={conditions?.items}
          careAgentName={careAgentName}
        />
      )}
    </>
  );
};
export default CcmRpmPatientBanner;
